import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  Site,
  Integration,
  MasterSlaveIntegration,
  SynchronizationJob,
  FortnoxAuthentication,
  FortnoxIntegrationSettings,
  FortnoxReconciliationSettings,
  FortnoxInvoicePaymentAccountSettings,
  FortnoxProductSynchronizationSettings,
  FortnoxOSSSettings,
  FortnoxExportAccountSettings,
  FortnoxGiftcardAccountSettings,
  FortnoxCustomerDebtAccount,
  FortnoxOrderListenerSettings,
  FortnoxInvoiceListenerSettings,
  RentmanAuthentication,
  RentmanIntegrationSettings,
  KustomAuthentication,
  KustomIntegrationSettings,
  LedyerAuthentication,
  LedyerIntegrationSettings,
  SitooAuthenticationInterface,
  SitooIntegrationSettings,
  VismaAuthentication,
  VismaIntegrationSettings,
  VismaInvoiceSettings,
  VismaInvoicePaymentSettings,
  VismaCustomerDebtAccount,
  WooCommerceAuthentication,
  WooCommerceIntegrationTimestamps,
  ProcessedTransaction,
  ProcessedPayout,
  FortnoxProcessedTransaction,
  FortnoxProcessedPayout,
  LoginCredentials,
  AuthResponse,
  Product,
  CsvSource, ShopifyIntegrationApiPollState, ShopifyAuthentication, ShopifyInventoryWebhook,
} from '@/types';

// Environment variables - fallback to original Vue.js env vars
const API_URL = import.meta.env.VITE_API_URL + 'api';
const WOO_API_URL = import.meta.env.VITE_WOO_API_URL + 'api';
const FORTNOX_API_URL = import.meta.env.VITE_FORTNOX_API_URL + 'api';
const KLARNA_API_URL = import.meta.env.VITE_KLARNA_API_URL + 'api';
const SITOO_API_URL = import.meta.env.VITE_SITOO_API_URL + 'api';
const VISMA_API_URL = import.meta.env.VITE_VISMA_API_URL + 'api';
const LEDYER_API_URL = import.meta.env.VITE_LEDYER_API_URL + 'api';
const RENTMAN_API_URL = import.meta.env.VITE_RENTMAN_API_URL + 'api';
const CSV_PRODUCT_API_URL = import.meta.env.VITE_CSV_PRODUCT_API_URL + 'api';
const SHOPIFY_API_URL = import.meta.env.VITE_SHOPIFY_API_URL + 'api';

// Create axios instances
const createClient = (baseURL: string): AxiosInstance => {
  return axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

const client = createClient(API_URL);
const wooClient = createClient(WOO_API_URL);
const shopifyClient = createClient(SHOPIFY_API_URL);
const kustomClient = createClient(KLARNA_API_URL);
const sitooClient = createClient(SITOO_API_URL);
const ledyerClient = createClient(LEDYER_API_URL);
const fortnoxClient = createClient(FORTNOX_API_URL);
const vismaClient = createClient(VISMA_API_URL);
const rentmanClient = createClient(RENTMAN_API_URL);
const csvProductClient = createClient(CSV_PRODUCT_API_URL);

// Token management
const getToken = (): string | null => {
  return localStorage.getItem('user-token');
};

const setToken = (token: string): void => {
  localStorage.setItem('user-token', token);
};

const removeToken = (): void => {
  localStorage.removeItem('user-token');
};

// Generic execute function
const execute = async <T>(
  client: AxiosInstance,
  method: 'get' | 'post' | 'put' | 'delete',
  resource: string,
  data?: any
): Promise<T> => {
  try {
    const token = getToken();
    const response: AxiosResponse<T> = await client({
      method,
      url: resource,
      data,
      headers: {
        Authorization: token ? `${token}` : '',
      },
    });

    if (response.status !== 200) {
      throw new Error('Request failed');
    }

    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Helper function to convert object to query string
const objectToQueryString = (obj: Record<string, any>): string => {
  return Object.keys(obj)
    .map(key => `${key}=${encodeURIComponent(obj[key])}`)
    .join('&');
};

// API class
class ApiService {


  // ========================================
  // USER API METHODS
  // ========================================
  async getUser(): Promise<User[]> {
    return execute<User[]>(client, 'get', '/users/current_user/');
  }
  // ========================================
  // CORE/SITES API METHODS
  // ========================================
  async getSites(): Promise<Site[]> {
    return execute<Site[]>(client, 'get', '/sites/');
  }

  async getSite(id: number): Promise<Site> {
    return execute<Site>(client, 'get', `/sites/${id}/`);
  }

  async createSite(data: Partial<Site>): Promise<Site> {
    return execute<Site>(client, 'post', '/sites/', data);
  }

  async updateSite(id: number, data: Partial<Site>): Promise<Site> {
    return execute<Site>(client, 'put', `/sites/${id}/`, data);
  }

  async deleteSite(id: number): Promise<void> {
    return execute<void>(client, 'delete', `/sites/${id}/`);
  }

  // ========================================
  // AUTHENTICATION API METHODS
  // ========================================
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return execute<AuthResponse>(client, 'post', '/users/login/', credentials);
  }

  async register(userData: { email: string; password: string }): Promise<any> {
    return execute<any>(client, 'post', '/users/register/', userData);
  }

  async logout(): Promise<void> {
    removeToken();
    return execute<void>(client, 'post', '/users/logout/');
  }

  // Token management methods
  getToken(): string | null {
    return getToken();
  }

  setToken(token: string): void {
    setToken(token);
  }

  removeToken(): void {
    removeToken();
  }

  // ========================================
  // INTEGRATION API METHODS
  // ========================================
  async getIntegrations(): Promise<Integration[]> {
    return execute<Integration[]>(client, 'get', '/integrations/');
  }

  async getIntegration(id: number): Promise<Integration> {
    return execute<Integration>(client, 'get', `/integrations/${id}/`);
  }

  async createIntegration(data: Partial<Integration>): Promise<Integration> {
    return execute<Integration>(client, 'post', '/integrations/', data);
  }

  async updateIntegration(id: number, data: Partial<Integration>): Promise<Integration> {
    return execute<Integration>(client, 'put', `/integrations/${id}/`, data);
  }

  async deleteIntegration(id: number): Promise<void> {
    return execute<void>(client, 'delete', `/integrations/${id}/`);
  }

  // ========================================
  // MASTER-SLAVE INTEGRATION API METHODS
  // ========================================
  async getMasterSlaveIntegrations(): Promise<MasterSlaveIntegration[]> {
    return execute<MasterSlaveIntegration[]>(client, 'get', '/master_slave_integrations/');
  }

  async getMasterSlaveIntegration(id: number): Promise<MasterSlaveIntegration> {
    return execute<MasterSlaveIntegration>(client, 'get', `/master_slave_integrations/${id}/`);
  }

  async createMasterSlaveIntegration(data: Partial<MasterSlaveIntegration>): Promise<MasterSlaveIntegration> {
    return execute<MasterSlaveIntegration>(client, 'post', '/master_slave_integrations/', data);
  }

  async updateMasterSlaveIntegration(id: number, data: Partial<MasterSlaveIntegration>): Promise<MasterSlaveIntegration> {
    return execute<MasterSlaveIntegration>(client, 'put', `/master_slave_integrations/${id}/`, data);
  }

  async deleteMasterSlaveIntegration(id: number): Promise<void> {
    return execute<void>(client, 'delete', `/master_slave_integrations/${id}/`);
  }

  // ========================================
  // SYNCHRONIZATION JOBS API METHODS
  // ========================================
  async getSynchronizationJobs(params?: Record<string, any>): Promise<{ objects: SynchronizationJob[], total: number, page: number }> {
    const queryString = params ? `?${objectToQueryString(params)}` : '';
    return execute<{ objects: SynchronizationJob[], total: number, page: number }>(client, 'get', `/synchronization_jobs/${queryString}`);
  }

  async searchSynchronizationJobs(params: Record<string, any>): Promise<{ objects: SynchronizationJob[], total: number, page: number }> {
    const queryString = objectToQueryString(params);
    return execute<{ objects: SynchronizationJob[], total: number, page: number }>(client, 'get', `/synchronization_jobs/?${queryString}`);
  }

  async getSynchronizationJob(id: number): Promise<SynchronizationJob> {
    return execute<SynchronizationJob>(client, 'get', `/synchronization_jobs/${id}/`);
  }

  async createSynchronizationJob(data: Partial<SynchronizationJob>): Promise<SynchronizationJob> {
    return execute<SynchronizationJob>(client, 'post', '/synchronization_jobs/', data);
  }

  async updateSynchronizationJob(id: number, data: Partial<SynchronizationJob>): Promise<SynchronizationJob> {
    return execute<SynchronizationJob>(client, 'put', `/synchronization_jobs/${id}/`, data);
  }

  async deleteSynchronizationJob(id: number): Promise<void> {
    return execute<void>(client, 'delete', `/synchronization_jobs/${id}/`);
  }

  // ========================================
  // FORTNOX API METHODS
  // ========================================

  // Fortnox Authentication
  async getFortnoxAuths(siteId: number): Promise<FortnoxAuthentication[]> {
    return execute<FortnoxAuthentication[]>(fortnoxClient, 'get', `/fortnox/auths/${siteId}/`);
  }

  async createFortnoxAuth(siteId: number, data: Partial<FortnoxAuthentication>): Promise<FortnoxAuthentication> {
    return execute<FortnoxAuthentication>(fortnoxClient, 'post', `/fortnox/auths/${siteId}/`, data);
  }

  async updateFortnoxAuth(siteId: number, data: Partial<FortnoxAuthentication>): Promise<FortnoxAuthentication> {
    return execute<FortnoxAuthentication>(fortnoxClient, 'put', `/fortnox/auths/${siteId}/`, data);
  }

  async deleteFortnoxAuth(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/auths/${siteId}/`);
  }

  // Fortnox OAuth2
  async getFortnoxOauth2s(siteId: number): Promise<any[]> {
    return execute<any[]>(fortnoxClient, 'get', `/fortnox/oauth2s/${siteId}/`);
  }

  // Fortnox Integration Settings
  async getFortnoxIntegrationSettings(siteId: number): Promise<FortnoxIntegrationSettings> {
    return execute<FortnoxIntegrationSettings>(fortnoxClient, 'get', `/fortnox/integration_settings/${siteId}/`);
  }

  async createFortnoxIntegrationSettings(siteId: number, data: FortnoxIntegrationSettings): Promise<FortnoxIntegrationSettings> {
    return execute<FortnoxIntegrationSettings>(fortnoxClient, 'post', `/fortnox/integration_settings/${siteId}/`, data);
  }

  async updateFortnoxIntegrationSettings(siteId: number, data: FortnoxIntegrationSettings): Promise<FortnoxIntegrationSettings> {
    return execute<FortnoxIntegrationSettings>(fortnoxClient, 'put', `/fortnox/integration_settings/${siteId}/`, data);
  }

  async deleteFortnoxIntegrationSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/integration_settings/${siteId}/`);
  }

  // Fortnox Reconciliation Settings
  async getFortnoxReconciliationSettings(siteId: number): Promise<FortnoxReconciliationSettings> {
    return execute<FortnoxReconciliationSettings>(fortnoxClient, 'get', `/fortnox/reconciliation_settings/${siteId}/`);
  }

  async createFortnoxReconciliationSettings(siteId: number, data: FortnoxReconciliationSettings): Promise<FortnoxReconciliationSettings> {
    return execute<FortnoxReconciliationSettings>(fortnoxClient, 'post', `/fortnox/reconciliation_settings/${siteId}/`, data);
  }

  async updateFortnoxReconciliationSettings(siteId: number, data: FortnoxReconciliationSettings): Promise<FortnoxReconciliationSettings> {
    return execute<FortnoxReconciliationSettings>(fortnoxClient, 'put', `/fortnox/reconciliation_settings/${siteId}/`, data);
  }

  async deleteFortnoxReconciliationSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/reconciliation_settings/${siteId}`);
  }

  // Fortnox Invoice Payment Account Settings
  async getFortnoxInvoicePaymentAccountSettings(siteId: number): Promise<FortnoxInvoicePaymentAccountSettings> {
    return execute<FortnoxInvoicePaymentAccountSettings>(fortnoxClient, 'get', `/fortnox/invoice_payment_account_settings/${siteId}/`);
  }

  async createFortnoxInvoicePaymentAccountSettings(siteId: number, data: FortnoxInvoicePaymentAccountSettings): Promise<FortnoxInvoicePaymentAccountSettings> {
    return execute<FortnoxInvoicePaymentAccountSettings>(fortnoxClient, 'post', `/fortnox/invoice_payment_account_settings/${siteId}/`, data);
  }

  async updateFortnoxInvoicePaymentAccountSettings(siteId: number, data: FortnoxInvoicePaymentAccountSettings): Promise<FortnoxInvoicePaymentAccountSettings> {
    return execute<FortnoxInvoicePaymentAccountSettings>(fortnoxClient, 'put', `/fortnox/invoice_payment_account_settings/${siteId}/`, data);
  }

  async deleteFortnoxInvoicePaymentAccountSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/invoice_payment_account_settings/${siteId}/`);
  }

  // Fortnox Price List Settings
  async getFortnoxProductSynchronizationSettings(siteId: number): Promise<FortnoxProductSynchronizationSettings> {
    return execute<FortnoxProductSynchronizationSettings>(fortnoxClient, 'get', `/fortnox/product_synchronization_settings/${siteId}/`);
  }

  async createFortnoxProductSynchronizationSettings(siteId: number, data: FortnoxProductSynchronizationSettings): Promise<FortnoxProductSynchronizationSettings> {
    return execute<FortnoxProductSynchronizationSettings>(fortnoxClient, 'post', `/fortnox/product_synchronization_settings/${siteId}/`, data);
  }

  async updateFortnoxProductSynchronizationSettings(siteId: number, data: FortnoxProductSynchronizationSettings): Promise<FortnoxProductSynchronizationSettings> {
    return execute<FortnoxProductSynchronizationSettings>(fortnoxClient, 'put', `/fortnox/product_synchronization_settings/${siteId}/`, data);
  }

  async deleteFortnoxProductSynchronizationSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/product_synchronization_settings/${siteId}/`);
  }

  // Fortnox OSS Settings
  async getFortnoxOSSSettings(siteId: number): Promise<FortnoxOSSSettings> {
    return execute<FortnoxOSSSettings>(fortnoxClient, 'get', `/fortnox/oss_settings/${siteId}/`);
  }

  async createFortnoxOSSSettings(siteId: number, data: FortnoxOSSSettings): Promise<FortnoxOSSSettings> {
    return execute<FortnoxOSSSettings>(fortnoxClient, 'post', `/fortnox/oss_settings/${siteId}/`, data);
  }

  async updateFortnoxOSSSettings(siteId: number, data: FortnoxOSSSettings): Promise<FortnoxOSSSettings> {
    return execute<FortnoxOSSSettings>(fortnoxClient, 'put', `/fortnox/oss_settings/${siteId}/`, data);
  }

  async deleteFortnoxOSSSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/oss_settings/${siteId}/`);
  }

  // Fortnox Export Account Settings
  async getFortnoxExportAccountSettings(siteId: number): Promise<FortnoxExportAccountSettings> {
    return execute<FortnoxExportAccountSettings>(fortnoxClient, 'get', `/fortnox/export_account_settings/${siteId}/`);
  }

  async createFortnoxExportAccountSettings(siteId: number, data: FortnoxExportAccountSettings): Promise<FortnoxExportAccountSettings> {
    return execute<FortnoxExportAccountSettings>(fortnoxClient, 'post', `/fortnox/export_account_settings/${siteId}/`, data);
  }

  async updateFortnoxExportAccountSettings(siteId: number, data: FortnoxExportAccountSettings): Promise<FortnoxExportAccountSettings> {
    return execute<FortnoxExportAccountSettings>(fortnoxClient, 'put', `/fortnox/export_account_settings/${siteId}/`, data);
  }

  async deleteFortnoxExportAccountSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/export_account_settings/${siteId}/`);
  }

  // Fortnox Giftcard Account Settings
  async getFortnoxGiftcardAccountSettings(siteId: number): Promise<FortnoxGiftcardAccountSettings> {
    return execute<FortnoxGiftcardAccountSettings>(fortnoxClient, 'get', `/fortnox/giftcard_account_settings/${siteId}/`);
  }

  async createFortnoxGiftcardAccountSettings(siteId: number, data: FortnoxGiftcardAccountSettings): Promise<FortnoxGiftcardAccountSettings> {
    return execute<FortnoxGiftcardAccountSettings>(fortnoxClient, 'post', `/fortnox/giftcard_account_settings/${siteId}/`, data);
  }

  async updateFortnoxGiftcardAccountSettings(siteId: number, data: FortnoxGiftcardAccountSettings): Promise<FortnoxGiftcardAccountSettings> {
    return execute<FortnoxGiftcardAccountSettings>(fortnoxClient, 'put', `/fortnox/giftcard_account_settings/${siteId}/`, data);
  }

  async deleteFortnoxGiftcardAccountSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/giftcard_account_settings/${siteId}`);
  }

  // Fortnox Customer Debt Accounts
  async getFortnoxCustomerDebtAccounts(siteId: number): Promise<FortnoxCustomerDebtAccount[]> {
    return execute<FortnoxCustomerDebtAccount[]>(fortnoxClient, 'get', `/fortnox/customer_debt_accounts/${siteId}/`);
  }

  async getFortnoxCustomerDebtAccount(siteId: number, accountId: number): Promise<FortnoxCustomerDebtAccount> {
    return execute<FortnoxCustomerDebtAccount>(fortnoxClient, 'get', `/fortnox/customer_debt_accounts/${siteId}/${accountId}/`);
  }

  async createFortnoxCustomerDebtAccount(siteId: number, data: FortnoxCustomerDebtAccount): Promise<FortnoxCustomerDebtAccount> {
    return execute<FortnoxCustomerDebtAccount>(fortnoxClient, 'post', `/fortnox/customer_debt_accounts/${siteId}/`, data);
  }

  async updateFortnoxCustomerDebtAccount(siteId: number, accountId: number, data: FortnoxCustomerDebtAccount): Promise<FortnoxCustomerDebtAccount> {
    return execute<FortnoxCustomerDebtAccount>(fortnoxClient, 'put', `/fortnox/customer_debt_accounts/${siteId}/${accountId}/`, data);
  }

  async deleteFortnoxCustomerDebtAccount(siteId: number, accountId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/customer_debt_accounts/${siteId}/${accountId}/`);
  }

  // Fortnox Order Listener Settings
  async getFortnoxOrderListenerSettings(siteId: number): Promise<FortnoxOrderListenerSettings> {
    return execute<FortnoxOrderListenerSettings>(fortnoxClient, 'get', `/fortnox/order_listener_settings/${siteId}/`);
  }

  async createFortnoxOrderListenerSettings(siteId: number, data: FortnoxOrderListenerSettings): Promise<FortnoxOrderListenerSettings> {
    return execute<FortnoxOrderListenerSettings>(fortnoxClient, 'post', `/fortnox/order_listener_settings/${siteId}/`, data);
  }

  async updateFortnoxOrderListenerSettings(siteId: number, data: FortnoxOrderListenerSettings): Promise<FortnoxOrderListenerSettings> {
    return execute<FortnoxOrderListenerSettings>(fortnoxClient, 'put', `/fortnox/order_listener_settings/${siteId}/`, data);
  }

  async deleteFortnoxOrderListenerSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/order_listener_settings/${siteId}/`);
  }

  // Fortnox Invoice Listener Settings
  async getFortnoxInvoiceListenerSettings(siteId: number): Promise<FortnoxInvoiceListenerSettings> {
    return execute<FortnoxInvoiceListenerSettings>(fortnoxClient, 'get', `/fortnox/invoice_listener_settings/${siteId}/`);
  }

  async createFortnoxInvoiceListenerSettings(siteId: number, data: FortnoxInvoiceListenerSettings): Promise<FortnoxInvoiceListenerSettings> {
    return execute<FortnoxInvoiceListenerSettings>(fortnoxClient, 'post', `/fortnox/invoice_listener_settings/${siteId}/`, data);
  }

  async updateFortnoxInvoiceListenerSettings(siteId: number, data: FortnoxInvoiceListenerSettings): Promise<FortnoxInvoiceListenerSettings> {
    return execute<FortnoxInvoiceListenerSettings>(fortnoxClient, 'put', `/fortnox/invoice_listener_settings/${siteId}/`, data);
  }

  async deleteFortnoxInvoiceListenerSettings(siteId: number): Promise<void> {
    return execute<void>(fortnoxClient, 'delete', `/fortnox/invoice_listener_settings/${siteId}/`);
  }

  // Fortnox Processed Data
  async getFortnoxProcessedTransactions(siteId: number, params?: Record<string, any>): Promise<{ objects: FortnoxProcessedTransaction[], page: number, total: number }> {
    const queryString = params ? `?${objectToQueryString(params)}` : '';
    return execute<{ objects: FortnoxProcessedTransaction[], page: number, total: number }>(fortnoxClient, 'get', `/fortnox/processed_transactions/${siteId}/${queryString}`);
  }

  async searchFortnoxProcessedTransactions(siteId: number, params: Record<string, any>): Promise<{ objects: FortnoxProcessedTransaction[], page: number, total: number }> {
    const queryString = objectToQueryString(params);
    return execute<{ objects: FortnoxProcessedTransaction[], page: number, total: number }>(fortnoxClient, 'get', `/fortnox/processed_transactions/${siteId}/?${queryString}`);
  }

  async getFortnoxProcessedPayouts(siteId: number, params?: Record<string, any>): Promise<{ objects: FortnoxProcessedPayout[], page: number, total: number }> {
    const queryString = params ? `?${objectToQueryString(params)}` : '';
    return execute<{ objects: FortnoxProcessedPayout[], page: number, total: number }>(fortnoxClient, 'get', `/fortnox/processed_payouts/${siteId}/${queryString}`);
  }

  async searchFortnoxProcessedPayouts(siteId: number, params: Record<string, any>): Promise<{ objects: FortnoxProcessedPayout[], page: number, total: number }> {
    const queryString = objectToQueryString(params);
    return execute<{ objects: FortnoxProcessedPayout[], page: number, total: number }>(fortnoxClient, 'get', `/fortnox/processed_payouts/${siteId}/?${queryString}`);
  }

  // ========================================
  // KLARNA API METHODS
  // ========================================

  // Kustom Authentication
  async getKustomAuths(siteId: number): Promise<KustomAuthentication[]> {
    return execute<KustomAuthentication[]>(kustomClient, 'get', `/kustom/auths/${siteId}/`);
  }

  async createKustomAuth(siteId: number, data: KustomAuthentication): Promise<KustomAuthentication> {
    return execute<KustomAuthentication>(kustomClient, 'post', `/kustom/auths/${siteId}/`, data);
  }

  async updateKustomAuth(siteId: number, kustomAuthId: number, data: Partial<KustomAuthentication>): Promise<KustomAuthentication> {
    return execute<KustomAuthentication>(kustomClient, 'put', `/kustom/auths/${siteId}/${kustomAuthId}/`, data);
  }

  async deleteKustomAuth(siteId: number, kustomAuthId: number): Promise<void> {
    return execute<void>(kustomClient, 'delete', `/kustom/auths/${siteId}/${kustomAuthId}/`);
  }

  // Kustom Integration Settings
  async getKustomIntegrationSettings(siteId: number): Promise<KustomIntegrationSettings> {
    return execute<KustomIntegrationSettings>(kustomClient, 'get', `/kustom/integration_settings/${siteId}/`);
  }

  async createKustomIntegrationSettings(siteId: number, data: KustomIntegrationSettings): Promise<KustomIntegrationSettings> {
    return execute<KustomIntegrationSettings>(kustomClient, 'post', `/kustom/integration_settings/${siteId}/`, data);
  }

  async updateKustomIntegrationSettings(siteId: number, data: KustomIntegrationSettings): Promise<KustomIntegrationSettings> {
    return execute<KustomIntegrationSettings>(kustomClient, 'put', `/kustom/integration_settings/${siteId}/`, data);
  }

  async deleteKustomIntegrationSettings(siteId: number): Promise<void> {
    return execute<void>(kustomClient, 'delete', `/kustom/integration_settings/${siteId}/`);
  }

  // ========================================
  // SITOO API METHODS
  // ========================================

  // Sitoo Authentication
  async getSitooAuthentication(siteId: number): Promise<SitooAuthenticationInterface> {
    return execute<SitooAuthenticationInterface>(sitooClient, 'get', `/sitoo/auths/${siteId}/`);
  }

  async createSitooAuthentication(siteId: number, data: SitooAuthenticationInterface): Promise<SitooAuthenticationInterface> {
    return execute<SitooAuthenticationInterface>(sitooClient, 'post', `/sitoo/auths/${siteId}/`, data);
  }

  async updateSitooAuthentication(siteId: number, data: SitooAuthenticationInterface): Promise<SitooAuthenticationInterface> {
    return execute<SitooAuthenticationInterface>(sitooClient, 'put', `/sitoo/auths/${siteId}/`, data);
  }

  async deleteSitooAuthentication(siteId: number): Promise<void> {
    return execute<void>(sitooClient, 'delete', `/sitoo/auths/${siteId}/`);
  }

  // Sitoo Integration Settings
  async getSitooIntegrationSettings(siteId: number): Promise<SitooIntegrationSettings> {
    return execute<SitooIntegrationSettings>(sitooClient, 'get', `/sitoo/integration_settings/${siteId}/`);
  }

  async createSitooIntegrationSettings(siteId: number, data: SitooIntegrationSettings): Promise<SitooIntegrationSettings> {
    return execute<SitooIntegrationSettings>(sitooClient, 'post', `/sitoo/integration_settings/${siteId}/`, data);
  }

  async updateSitooIntegrationSettings(siteId: number, data: SitooIntegrationSettings): Promise<SitooIntegrationSettings> {
    return execute<SitooIntegrationSettings>(sitooClient, 'put', `/sitoo/integration_settings/${siteId}/`, data);
  }

  async deleteSitooIntegrationSettings(siteId: number): Promise<void> {
    return execute<void>(sitooClient, 'delete', `/sitoo/integration_settings/${siteId}/`);
  }

  // ========================================
  // LEDYER API METHODS
  // ========================================

  // Ledyer Authentication
  async getLedyerAuths(siteId: number): Promise<LedyerAuthentication[]> {
    return execute<LedyerAuthentication[]>(ledyerClient, 'get', `/ledyer/auths/${siteId}/`);
  }

  async createLedyerAuth(siteId: number, data: LedyerAuthentication): Promise<LedyerAuthentication> {
    return execute<LedyerAuthentication>(ledyerClient, 'post', `/ledyer/auths/${siteId}/`, data);
  }

  async updateLedyerAuth(siteId: number, data: LedyerAuthentication): Promise<LedyerAuthentication> {
    return execute<LedyerAuthentication>(ledyerClient, 'put', `/ledyer/auths/${siteId}/`, data);
  }

  async deleteLedyerAuth(siteId: number): Promise<void> {
    return execute<void>(ledyerClient, 'delete', `/ledyer/auths/${siteId}/`);
  }

  // Ledyer Integration Settings
  async getLedyerIntegrationSettings(siteId: number): Promise<LedyerIntegrationSettings> {
    return execute<LedyerIntegrationSettings>(ledyerClient, 'get', `/ledyer/integration_settings/${siteId}/`);
  }

  async createLedyerIntegrationSettings(siteId: number, data: LedyerIntegrationSettings): Promise<LedyerIntegrationSettings> {
    return execute<LedyerIntegrationSettings>(ledyerClient, 'post', `/ledyer/integration_settings/${siteId}/`, data);
  }

  async updateLedyerIntegrationSettings(siteId: number, data: LedyerIntegrationSettings): Promise<LedyerIntegrationSettings> {
    return execute<LedyerIntegrationSettings>(ledyerClient, 'put', `/ledyer/integration_settings/${siteId}/`, data);
  }

  async deleteLedyerIntegrationSettings(siteId: number): Promise<void> {
    return execute<void>(ledyerClient, 'delete', `/ledyer/integration_settings/${siteId}/`);
  }

  // ========================================
  // VISMA API METHODS
  // ========================================

  // Visma Authentication
  async getVismaAuthCredentials(siteId: number): Promise<VismaAuthentication> {
    return execute<VismaAuthentication>(vismaClient, 'get', `/visma/auth_credentials/${siteId}/`);
  }

  async createVismaAuthCredentials(siteId: number, data: Partial<VismaAuthentication>): Promise<VismaAuthentication> {
    return execute<VismaAuthentication>(vismaClient, 'post', `/visma/auth_credentials/${siteId}/`, data);
  }

  async updateVismaAuthCredentials(siteId: number, data: Partial<VismaAuthentication>): Promise<VismaAuthentication> {
    return execute<VismaAuthentication>(vismaClient, 'put', `/visma/auth_credentials/${siteId}/`, data);
  }

  async deleteVismaAuthCredentials(siteId: number): Promise<void> {
    return execute<void>(vismaClient, 'delete', `/visma/auth_credentials/${siteId}/`);
  }

  async getVismaAuthUrl(): Promise<{ auth_url: string }> {
    return execute<{ auth_url: string }>(vismaClient, 'get', `/visma/auth_url/`);
  }

  // Visma Customer Debt Accounts
  async getVismaCustomerDebtAccounts(siteId: number): Promise<VismaCustomerDebtAccount[]> {
    return execute<VismaCustomerDebtAccount[]>(vismaClient, 'get', `/visma/customer_debt_accounts/${siteId}/`);
  }

  async getVismaCustomerDebtAccount(siteId: number, accountId: number): Promise<VismaCustomerDebtAccount> {
    return execute<VismaCustomerDebtAccount>(vismaClient, 'get', `/visma/customer_debt_accounts/${siteId}/${accountId}/`);
  }

  async createVismaCustomerDebtAccount(siteId: number, data: VismaCustomerDebtAccount): Promise<VismaCustomerDebtAccount> {
    return execute<VismaCustomerDebtAccount>(vismaClient, 'post', `/visma/customer_debt_accounts/${siteId}/`, data);
  }

  async updateVismaCustomerDebtAccount(siteId: number, accountId: number, data: VismaCustomerDebtAccount): Promise<VismaCustomerDebtAccount> {
    return execute<VismaCustomerDebtAccount>(vismaClient, 'put', `/visma/customer_debt_accounts/${siteId}/${accountId}/`, data);
  }

  async deleteVismaCustomerDebtAccount(siteId: number, accountId: number): Promise<void> {
    return execute<void>(vismaClient, 'delete', `/visma/customer_debt_accounts/${siteId}/${accountId}/`);
  }

  // Visma Integration Settings
  async getVismaIntegrationSettings(siteId: number): Promise<VismaIntegrationSettings> {
    return execute<VismaIntegrationSettings>(vismaClient, 'get', `/visma/integration_settings/${siteId}/`);
  }

  async createVismaIntegrationSettings(siteId: number, data: VismaIntegrationSettings): Promise<VismaIntegrationSettings> {
    return execute<VismaIntegrationSettings>(vismaClient, 'post', `/visma/integration_settings/${siteId}/`, data);
  }

  async updateVismaIntegrationSettings(siteId: number, data: VismaIntegrationSettings): Promise<VismaIntegrationSettings> {
    return execute<VismaIntegrationSettings>(vismaClient, 'put', `/visma/integration_settings/${siteId}/`, data);
  }

  async deleteVismaIntegrationSettings(siteId: number): Promise<void> {
    return execute<void>(vismaClient, 'delete', `/visma/integration_settings/${siteId}/`);
  }

  // Visma Invoice Settings
  async getVismaInvoiceSettings(siteId: number): Promise<VismaInvoiceSettings> {
    return execute<VismaInvoiceSettings>(vismaClient, 'get', `/visma/invoice_settings/${siteId}/`);
  }

  async createVismaInvoiceSettings(siteId: number, data: VismaInvoiceSettings): Promise<VismaInvoiceSettings> {
    return execute<VismaInvoiceSettings>(vismaClient, 'post', `/visma/invoice_settings/${siteId}/`, data);
  }

  async updateVismaInvoiceSettings(siteId: number, data: VismaInvoiceSettings): Promise<VismaInvoiceSettings> {
    return execute<VismaInvoiceSettings>(vismaClient, 'put', `/visma/invoice_settings/${siteId}/`, data);
  }

  async deleteVismaInvoiceSettings(siteId: number): Promise<void> {
    return execute<void>(vismaClient, 'delete', `/visma/invoice_settings/${siteId}/`);
  }

  // Visma Invoice Payment Settings
  async getVismaInvoicePaymentSettings(siteId: number): Promise<VismaInvoicePaymentSettings> {
    return execute<VismaInvoicePaymentSettings>(vismaClient, 'get', `/visma/invoice_payment_settings/${siteId}/`);
  }

  async createVismaInvoicePaymentSettings(siteId: number, data: VismaInvoicePaymentSettings): Promise<VismaInvoicePaymentSettings> {
    return execute<VismaInvoicePaymentSettings>(vismaClient, 'post', `/visma/invoice_payment_settings/${siteId}/`, data);
  }

  async updateVismaInvoicePaymentSettings(siteId: number, data: VismaInvoicePaymentSettings): Promise<VismaInvoicePaymentSettings> {
    return execute<VismaInvoicePaymentSettings>(vismaClient, 'put', `/visma/invoice_payment_settings/${siteId}/`, data);
  }

  async deleteVismaInvoicePaymentSettings(siteId: number): Promise<void> {
    return execute<void>(vismaClient, 'delete', `/visma/invoice_payment_settings/${siteId}/`);
  }

  // Visma Processed Data
  async getVismaProcessedTransactions(siteId: number, params?: Record<string, any>): Promise<{ objects: ProcessedTransaction[], page: number, total: number }> {
    const queryString = params ? `?${objectToQueryString(params)}` : '';
    return execute<{ objects: ProcessedTransaction[], page: number, total: number }>(vismaClient, 'get', `/visma/processed_transactions/${siteId}/${queryString}`);
  }

  async searchVismaProcessedTransactions(siteId: number, params: Record<string, any>): Promise<{ objects: ProcessedTransaction[], page: number, total: number }> {
    const queryString = objectToQueryString(params);
    return execute<{ objects: ProcessedTransaction[], page: number, total: number }>(vismaClient, 'get', `/visma/processed_transactions/${siteId}/?${queryString}`);
  }

  async getVismaProcessedPayouts(siteId: number, params?: Record<string, any>): Promise<{ objects: ProcessedPayout[], page: number, total: number }> {
    const queryString = params ? `?${objectToQueryString(params)}` : '';
    return execute<{ objects: ProcessedPayout[], page: number, total: number }>(vismaClient, 'get', `/visma/processed_payouts/${siteId}/${queryString}`);
  }

  async searchVismaProcessedPayouts(siteId: number, params: Record<string, any>): Promise<{ objects: ProcessedPayout[], page: number, total: number }> {
    const queryString = objectToQueryString(params);
    return execute<{ objects: ProcessedPayout[], page: number, total: number }>(vismaClient, 'get', `/visma/processed_payouts/${siteId}/?${queryString}`);
  }

  async getVismaSettings(siteId: number): Promise<{ objects: ProcessedPayout[], page: number, total: number }> {

    return execute<{ objects: ProcessedPayout[], page: number, total: number }>(vismaClient, 'get', `/visma/settings/${siteId}/`);
  }

  // ========================================
  // RENTMAN API METHODS
  // ========================================

  // Rentman Authentication
  async getRentmanAuthentication(siteId: number): Promise<RentmanAuthentication[]> {
    return execute<RentmanAuthentication[]>(rentmanClient, 'get', `/rentman/auth_credentials/${siteId}/`);
  }

  async createRentmanAuthentication(siteId: number, data: RentmanAuthentication): Promise<RentmanAuthentication> {
    return execute<RentmanAuthentication>(rentmanClient, 'post', `/rentman/auth_credentials/${siteId}/`, data);
  }

  async updateRentmanAuthentication(siteId: number, data: RentmanAuthentication): Promise<RentmanAuthentication> {
    return execute<RentmanAuthentication>(rentmanClient, 'put', `/rentman/auth_credentials/${siteId}/`, data);
  }

  async deleteRentmanAuthentication(siteId: number): Promise<void> {
    return execute<void>(rentmanClient, 'delete', `/rentman/auth_credentials/${siteId}/`);
  }

  // Rentman Integration Settings
  async getRentmanIntegrationSettings(siteId: number): Promise<RentmanIntegrationSettings> {
    return execute<RentmanIntegrationSettings>(rentmanClient, 'get', `/rentman/integration_settings/${siteId}/`);
  }

  async createRentmanIntegrationSettings(siteId: number, data: RentmanIntegrationSettings): Promise<RentmanIntegrationSettings> {
    return execute<RentmanIntegrationSettings>(rentmanClient, 'post', `/rentman/integration_settings/${siteId}/`, data);
  }

  async updateRentmanIntegrationSettings(siteId: number, data: RentmanIntegrationSettings): Promise<RentmanIntegrationSettings> {
    return execute<RentmanIntegrationSettings>(rentmanClient, 'put', `/rentman/integration_settings/${siteId}/`, data);
  }

  async deleteRentmanIntegrationSettings(siteId: number): Promise<void> {
    return execute<void>(rentmanClient, 'delete', `/rentman/integration_settings/${siteId}/`);
  }

  // ========================================
  // WOOCOMMERCE API METHODS
  // ========================================

  // WooCommerce Authentication
  async getWooCommerceAuthentications(siteId: number): Promise<WooCommerceAuthentication[]> {
    return execute<WooCommerceAuthentication[]>(wooClient, 'get', `/woocommerce/auth_settings/${siteId}/`);
  }

  async getWooCommerceAuthentication(siteId: number): Promise<WooCommerceAuthentication> {
    return execute<WooCommerceAuthentication>(wooClient, 'get', `/woocommerce/auth_settings/${siteId}/`);
  }

  async createWooCommerceAuthentication(siteId: number, data: WooCommerceAuthentication): Promise<WooCommerceAuthentication> {
    return execute<WooCommerceAuthentication>(wooClient, 'post', `/woocommerce/auth_settings/${siteId}/`, data);
  }

  async updateWooCommerceAuthentication(siteId: number, data: WooCommerceAuthentication): Promise<WooCommerceAuthentication> {
    return execute<WooCommerceAuthentication>(wooClient, 'put', `/woocommerce/auth_settings/${siteId}/`, data);
  }

  async deleteWooCommerceAuthentication(siteId: number): Promise<void> {
    return execute<void>(wooClient, 'delete', `/auth_settings/${siteId}/`);
  }

  // WooCommerce Integration Timestamps
  async getWooCommerceIntegrationTimestamps(siteId: number): Promise<WooCommerceIntegrationTimestamps[]> {
    return execute<WooCommerceIntegrationTimestamps[]>(wooClient, 'get', `/woocommerce/latest_poll_times/${siteId}/`);
  }

  async getWooCommerceIntegrationTimestamp(siteId: number): Promise<WooCommerceIntegrationTimestamps> {
    return execute<WooCommerceIntegrationTimestamps>(wooClient, 'get', `/woocommerce/latest_poll_times/${siteId}/`);
  }

  async createWooCommerceIntegrationTimestamps(siteId: number, data: WooCommerceIntegrationTimestamps): Promise<WooCommerceIntegrationTimestamps> {
    return execute<WooCommerceIntegrationTimestamps>(wooClient, 'post', `/woocommerce/latest_poll_times/${siteId}/`, data);
  }

  async updateWooCommerceIntegrationTimestamps(siteId: number, data: WooCommerceIntegrationTimestamps): Promise<WooCommerceIntegrationTimestamps> {
    return execute<WooCommerceIntegrationTimestamps>(wooClient, 'put', `/woocommerce/latest_poll_times/${siteId}/`, data);
  }

  async deleteWooCommerceIntegrationTimestamps(siteId: number): Promise<void> {
    return execute<void>(wooClient, 'delete', `/woocommerce/latest_poll_times/${siteId}/`);
  }

  // ========================================
  // SHOPIFY API METHODS
  // ========================================

  // Shopify Authentication
  async getShopifyAuthentications(): Promise<ShopifyAuthentication[]> {
    return execute<ShopifyAuthentication[]>(shopifyClient, 'get', `/shopify/authentications/`);
  }

  async getShopifyAuthentication(): Promise<ShopifyAuthentication> {
    return execute<ShopifyAuthentication>(shopifyClient, 'get', `/shopify/authentications/`);
  }

  async createShopifyAuthentication(data: ShopifyAuthentication): Promise<ShopifyAuthentication> {
    return execute<ShopifyAuthentication>(shopifyClient, 'post', `/shopify/authentications/`, data);
  }

  async updateShopifyAuthentication(data: ShopifyAuthentication): Promise<ShopifyAuthentication> {
    return execute<ShopifyAuthentication>(shopifyClient, 'put', `/shopify/authentications/`, data);
  }

  async deleteShopifyAuthentication(): Promise<void> {
    return execute<void>(shopifyClient, 'delete', `/shopify/authentications/`);
  }

  // Shopify Integration Timestamps
  async getShopifyIntegrationApiPollState(): Promise<ShopifyIntegrationApiPollState> {
    return execute<ShopifyIntegrationApiPollState>(shopifyClient, 'get', `/shopify/api_poll_states/`);
  }

  async createShopifyIntegrationApiPollState(data: ShopifyIntegrationApiPollState): Promise<ShopifyIntegrationApiPollState> {
    return execute<ShopifyIntegrationApiPollState>(shopifyClient, 'post', `/shopify/api_poll_states/`, data);
  }

  async updateShopifyIntegrationApiPollState(data: ShopifyIntegrationApiPollState): Promise<ShopifyIntegrationApiPollState> {
    return execute<ShopifyIntegrationApiPollState>(shopifyClient, 'put', `/shopify/api_poll_states/`, data);
  }

  async deleteShopifyIntegrationApiPollState(): Promise<void> {
    return execute<void>(shopifyClient, 'delete', `/shopify/api_poll_states/`);
  }

  // Shopify Api Token
  async clearApiToken(): Promise<void> {
    return execute<void>(shopifyClient, 'delete', `/shopify/api_tokens/`);
  }

  // Shopify Webhooks
  async getShopifyInventoryWebhooks(): Promise<ShopifyInventoryWebhook[]> {
    return execute<ShopifyInventoryWebhook[]>(shopifyClient, 'get', `/shopify/webhooks/`);
  }

  async createShopifyInventoryWebhook(): Promise<void> {
    return execute<void>(shopifyClient, 'post', `/shopify/webhooks/register/inventory`);
  }

  async clearShopifyInventoryWebhooks(): Promise<void> {
    return execute<void>(shopifyClient, 'delete', `/shopify/webhooks/`);
  }

  // ========================================
  // PRODUCTS API METHODS
  // ========================================
  async getProducts(params?: Record<string, any>): Promise<{ objects: Product[], total: number, page: number }> {
    const queryString = params ? `?${objectToQueryString(params)}` : '';
    return execute<{ objects: Product[], total: number, page: number }>(client, 'get', `/products/${queryString}`);
  }

  async searchProducts(params: Record<string, any>): Promise<{ objects: Product[], total: number, page: number }> {
    const queryString = objectToQueryString(params);
    return execute<{ objects: Product[], total: number, page: number }>(client, 'get', `/products/?${queryString}`);
  }

  async getProduct(id: number): Promise<Product> {
    return execute<Product>(client, 'get', `/products/${id}/`);
  }

  async createProduct(data: Partial<Product>): Promise<Product> {
    return execute<Product>(client, 'post', '/products/', data);
  }

  async updateProduct(id: number, data: Partial<Product>): Promise<Product> {
    return execute<Product>(client, 'put', `/products/${id}/`, data);
  }

  async deleteProduct(id: number): Promise<void> {
    return execute<void>(client, 'delete', `/products/${id}/`);
  }

  // ========================================
  // CSV SOURCE API METHODS
  // ========================================
  async getCsvSources(params?: Record<string, any>): Promise<{ objects: CsvSource[], total: number, page: number }> {
    const queryString = params ? `?${objectToQueryString(params)}` : '';
    return execute<{ objects: CsvSource[], total: number, page: number }>(csvProductClient, 'get', `/csv_product_service/csv_sources/${queryString}`);
  }

  async getCsvSource(): Promise<CsvSource> {
    return execute<CsvSource>(csvProductClient, 'get', `/csv_product_service/csv_sources/`);
  }

  async createCsvSource(data: Partial<CsvSource>): Promise<CsvSource> {
    return execute<CsvSource>(csvProductClient, 'post', '/csv_product_service/csv_sources/', data);
  }

  async updateCsvSource(data: Partial<CsvSource>): Promise<CsvSource> {
    return execute<CsvSource>(csvProductClient, 'put', `/csv_product_service/csv_sources/`, data);
  }

  async deleteCsvSource(): Promise<void> {
    return execute<void>(csvProductClient, 'delete', `/csv_product_service/csv_sources/`);
  }

  async parseHeaders(data: { source_url: string; username?: string; password?: string }): Promise<string[]> {
    return execute<string[]>(csvProductClient, 'post', `/csv_product_service/csv_sources/parse_headers/`, data);
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
